/**
 * WebReorderPageViewModel - Web Platform Adapter
 *
 * Web-specific adapter for ReorderPageViewModel.
 * Wires the shared VM with web platform services, Zustand stores,
 * and the openReorderProductSheet action from useUIStore.
 */

import { useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createReorderPageViewModel, createWebPlatformServices } from '@restaurant/viewmodels'
import {
  useOrderStore,
  useRestaurantStore,
  useUIStore,
  useCartStore,
} from '@restaurant/core'
import type { MenuItem } from '@restaurant/core'
// The cart store's CustomizationSelection ({ customizationItemId, selectedOptionId })
// is what cartStore.addItem consumes — distinct from the @restaurant/core barrel's
// canonical CustomizationSelection (the menu.service { customizationId, optionId }).
import type { CustomizationSelection } from '@restaurant/core/stores/cartStore'
import type { SelectedOption } from '@restaurant/core'

/**
 * Maps an OrderItem's selectedOptions (order model) to CustomizationSelection[]
 * (cart model).
 *
 * The order model stores selected customizations as:
 *   SelectedOption { optionTitle, selectedItems: CustomizationItem[] }
 * where CustomizationItem = { customizationItemId, name, additionalPrice }.
 *
 * The cart expects:
 *   CustomizationSelection { customizationItemId, selectedOptionId }
 *
 * Since the order model doesn't preserve the customization group ID separately,
 * we use customizationItemId for both fields. The cart's price calculation
 * for reordered items may not find the customization in menuItem.customizations
 * (ClientMenuItem lacks that field), but the item is still added correctly
 * and the user can adjust via the product sheet.
 */
function mapSelectedOptionsToCustomizations(
  selectedOptions: SelectedOption[],
): CustomizationSelection[] {
  const selections: CustomizationSelection[] = []

  for (const option of selectedOptions) {
    for (const item of option.selectedItems) {
      selections.push({
        customizationItemId: item.customizationItemId,
        selectedOptionId: item.customizationItemId,
      })
    }
  }

  return selections
}

/**
 * Web adapter for ReorderPageViewModel.
 * Injects web platform services, order/restaurant/cart/UI store state,
 * and wires navigation and action callbacks.
 */
export const useWebReorderPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Order store
  const orderHistory = useOrderStore((s) => s.orderHistory)
  const isLoadingHistory = useOrderStore((s) => s.isLoadingHistory)
  const getOrderById = useOrderStore((s) => s.getOrderById)

  // Restaurant store — for location name lookup
  const locations = useRestaurantStore((s) => s.locations)

  // UI store — reorder product sheet
  const openReorderProductSheet = useUIStore((s) => s.openReorderProductSheet)

  // Cart store — add items
  const addItem = useCartStore((s) => s.addItem)

  // Memoised platform services
  const platformServices = useMemo(
    () => createWebPlatformServices(navigate, location),
    [navigate, location],
  )

  // Location name resolver
  const getLocationName = useCallback(
    (merchantId: string): string => {
      if (!merchantId || !locations.length) return 'Restaurant'
      const loc = locations.find((l) => l.merchantId === merchantId || l.originalMerchantId === merchantId)
      return loc?.name || 'Restaurant'
    },
    [locations],
  )

  // Callbacks
  const onProductPress = useCallback(
    (productId: string, orderId: string, itemId: string) => {
      openReorderProductSheet({ productId, orderId, itemId })
    },
    [openReorderProductSheet],
  )

  const onAddAllPress = useCallback(
    (orderId: string) => {
      const order = getOrderById(orderId)
      if (!order) return

      for (const item of order.items) {
        const customizations = mapSelectedOptionsToCustomizations(item.selectedOptions)
        addItem(
          item.menuItem as unknown as MenuItem,
          item.quantity,
          customizations,
          { selectedPriceVariant: item.selectedPriceVariant },
        )
      }

      navigate('/cart')
    },
    [getOrderById, addItem, navigate],
  )

  const onDetailsPress = useCallback(
    (orderId: string) => {
      navigate(`/orders/${orderId}/details`)
    },
    [navigate],
  )

  // Hook that provides order data for the shared VM
  const useOrderData = useCallback(
    () => ({ orders: orderHistory, loading: isLoadingHistory }),
    [orderHistory, isLoadingHistory],
  )

  // Create and invoke the shared hook
  const useReorderPage = useMemo(
    () =>
      createReorderPageViewModel(platformServices, {
        useOrderData,
        getLocationName,
        getOrderById,
        onProductPress,
        onAddAllPress,
        onDetailsPress,
      }),
    [
      platformServices,
      useOrderData,
      getLocationName,
      getOrderById,
      onProductPress,
      onAddAllPress,
      onDetailsPress,
    ],
  )

  return useReorderPage()
}
